<template>
  <div
    class="result-container"
    :class="[
      `result-${status}`,
      {
        'result-compact': size === 'small'
      }
    ]"
    :style="containerStyle"
  >
    <!-- 状态图标 -->
    <div class="result-icon">
      <!-- 自定义图标 -->
      <div v-if="icon" class="result-custom-icon">
        <ElIcon :size="iconSize">
          <component :is="icon" />
        </ElIcon>
      </div>

      <!-- 预设状态图标 -->
      <div v-else class="result-status-icon">
        <!-- 成功状态 -->
        <div v-if="status === 'success'" class="result-success">
          <ElIcon :size="iconSize" color="#67c23a">
            <SuccessFilled />
          </ElIcon>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="status === 'error'" class="result-error">
          <ElIcon :size="iconSize" color="#f56c6c">
            <CircleCloseFilled />
          </ElIcon>
        </div>

        <!-- 警告状态 -->
        <div v-else-if="status === 'warning'" class="result-warning">
          <ElIcon :size="iconSize" color="#e6a23c">
            <WarningFilled />
          </ElIcon>
        </div>

        <!-- 信息状态 -->
        <div v-else-if="status === 'info'" class="result-info">
          <ElIcon :size="iconSize" color="#409eff">
            <InfoFilled />
          </ElIcon>
        </div>

        <!-- 404状态 -->
        <div v-else-if="status === '404'" class="result-404">
          <div class="result-404-icon">
            <span class="result-404-text">404</span>
          </div>
        </div>

        <!-- 403状态 -->
        <div v-else-if="status === '403'" class="result-403">
          <div class="result-403-icon">
            <span class="result-403-text">403</span>
          </div>
        </div>

        <!-- 500状态 -->
        <div v-else-if="status === '500'" class="result-500">
          <div class="result-500-icon">
            <span class="result-500-text">500</span>
          </div>
        </div>

        <!-- 自定义状态 -->
        <div v-else class="result-custom">
          <ElIcon :size="iconSize" color="#909399">
            <QuestionFilled />
          </ElIcon>
        </div>
      </div>

      <!-- 插槽：自定义图标 -->
      <slot name="icon"></slot>
    </div>

    <!-- 标题 -->
    <div v-if="title || $slots.title" class="result-title">
      <slot name="title">
        {{ title }}
      </slot>
    </div>

    <!-- 副标题 -->
    <div v-if="subTitle || $slots.subTitle" class="result-subtitle">
      <slot name="subTitle">
        {{ subTitle }}
      </slot>
    </div>

    <!-- 详细描述 -->
    <div v-if="description || $slots.description" class="result-description">
      <slot name="description">
        {{ description }}
      </slot>
    </div>

    <!-- 操作按钮区域 -->
    <div v-if="showActions || $slots.extra" class="result-actions">
      <slot name="extra">
        <!-- 主要操作按钮 -->
        <ElButton
          v-if="primaryActionText"
          :type="primaryActionType"
          :size="actionSize"
          :icon="primaryActionIcon"
          @click="handlePrimaryAction"
        >
          {{ primaryActionText }}
        </ElButton>

        <!-- 次要操作按钮 -->
        <ElButton
          v-if="secondaryActionText"
          :size="actionSize"
          @click="handleSecondaryAction"
        >
          {{ secondaryActionText }}
        </ElButton>

        <!-- 返回按钮 -->
        <ElButton
          v-if="showBackButton"
          :size="actionSize"
          @click="handleBack"
        >
          {{ backButtonText }}
        </ElButton>
      </slot>
    </div>

    <!-- 额外内容 -->
    <div v-if="$slots.content" class="result-content">
      <slot name="content"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Result 结果页组件
 * 用于显示操作结果、错误页面等场景
 * @author AI Assistant
 * @date 2024-01-01
 */

import { computed } from 'vue'
import { useRouter } from 'vue-router'
import type { Component } from 'vue'
import type { ComponentSize } from 'element-plus'
import {
  SuccessFilled,
  CircleCloseFilled,
  WarningFilled,
  InfoFilled,
  QuestionFilled
} from '@element-plus/icons-vue'

// 接口定义
interface Props {
  /** 结果状态 */
  status?: 'success' | 'error' | 'warning' | 'info' | '404' | '403' | '500' | 'custom'
  /** 标题 */
  title?: string
  /** 副标题 */
  subTitle?: string
  /** 详细描述 */
  description?: string
  /** 自定义图标 */
  icon?: Component
  /** 图标大小 */
  iconSize?: number
  /** 组件大小 */
  size?: ComponentSize | 'mini'
  /** 是否显示操作按钮 */
  showActions?: boolean
  /** 主要操作按钮文本 */
  primaryActionText?: string
  /** 主要操作按钮类型 */
  primaryActionType?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
  /** 主要操作按钮图标 */
  primaryActionIcon?: Component
  /** 次要操作按钮文本 */
  secondaryActionText?: string
  /** 操作按钮大小 */
  actionSize?: ComponentSize
  /** 是否显示返回按钮 */
  showBackButton?: boolean
  /** 返回按钮文本 */
  backButtonText?: string
  /** 容器最小高度 */
  minHeight?: string
  /** 容器宽度 */
  width?: string
}

interface Emits {
  /** 主要操作点击事件 */
  'primary-action': []
  /** 次要操作点击事件 */
  'secondary-action': []
  /** 返回按钮点击事件 */
  'back': []
}

// 定义 props
const props = withDefaults(defineProps<Props>(), {
  status: 'info',
  title: '',
  subTitle: '',
  description: '',
  iconSize: 72,
  size: 'default',
  showActions: false,
  primaryActionText: '',
  primaryActionType: 'primary',
  secondaryActionText: '',
  actionSize: 'default',
  showBackButton: false,
  backButtonText: '返回',
  minHeight: '400px',
  width: '100%'
})

// 定义 emits
const emit = defineEmits<Emits>()

// 路由实例
const router = useRouter()

// 计算属性
const containerStyle = computed(() => {
  const sizeMap = {
    mini: '300px',
    small: '350px',
    default: '400px',
    large: '500px'
  }

  return {
    minHeight: props.size in sizeMap ? sizeMap[props.size as keyof typeof sizeMap] : props.minHeight,
    width: props.width
  }
})

// 默认标题映射
const defaultTitles = computed(() => ({
  success: '操作成功',
  error: '操作失败',
  warning: '警告',
  info: '提示信息',
  '404': '页面不存在',
  '403': '访问被拒绝',
  '500': '服务器错误',
  custom: '自定义状态'
}))

// 默认副标题映射
const defaultSubTitles = computed(() => ({
  success: '您的操作已成功完成',
  error: '抱歉，操作执行失败',
  warning: '请注意相关提示信息',
  info: '这里是一些提示信息',
  '404': '抱歉，您访问的页面不存在',
  '403': '抱歉，您没有权限访问此页面',
  '500': '抱歉，服务器出现错误',
  custom: '自定义状态描述'
}))

// 实际显示的标题
const displayTitle = computed(() => {
  return props.title || defaultTitles.value[props.status]
})

// 实际显示的副标题
const displaySubTitle = computed(() => {
  return props.subTitle || defaultSubTitles.value[props.status]
})

// 方法定义
/**
 * 处理主要操作点击
 */
const handlePrimaryAction = (): void => {
  emit('primary-action')
}

/**
 * 处理次要操作点击
 */
const handleSecondaryAction = (): void => {
  emit('secondary-action')
}

/**
 * 处理返回按钮点击
 */
const handleBack = (): void => {
  emit('back')
  // 默认行为：返回上一页
  if (window.history.length > 1) {
    router.back()
  } else {
    router.push('/')
  }
}

// 暴露方法
defineExpose({
  goBack: handleBack,
  triggerPrimaryAction: handlePrimaryAction,
  triggerSecondaryAction: handleSecondaryAction
})
</script>

<style scoped>
.result-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  background: var(--el-bg-color);
}

.result-compact {
  padding: 32px 16px;
}

.result-icon {
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.result-custom-icon,
.result-status-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 错误状态样式 */
.result-404-icon,
.result-403-icon,
.result-500-icon {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  font-weight: bold;
  color: #fff;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
}

.result-404-text,
.result-403-text,
.result-500-text {
  font-family: 'Arial', sans-serif;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.result-title {
  font-size: 24px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  margin-bottom: 12px;
  line-height: 1.4;
}

.result-subtitle {
  font-size: 16px;
  color: var(--el-text-color-regular);
  margin-bottom: 16px;
  line-height: 1.5;
}

.result-description {
  font-size: 14px;
  color: var(--el-text-color-secondary);
  margin-bottom: 24px;
  line-height: 1.6;
  max-width: 500px;
}

.result-actions {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.result-content {
  width: 100%;
  max-width: 600px;
}

/* 状态特定样式 */
.result-success .result-title {
  color: var(--el-color-success);
}

.result-error .result-title {
  color: var(--el-color-danger);
}

.result-warning .result-title {
  color: var(--el-color-warning);
}

.result-info .result-title {
  color: var(--el-color-info);
}

/* 紧凑模式样式 */
.result-compact .result-icon {
  margin-bottom: 16px;
}

.result-compact .result-title {
  font-size: 20px;
  margin-bottom: 8px;
}

.result-compact .result-subtitle {
  font-size: 14px;
  margin-bottom: 12px;
}

.result-compact .result-description {
  font-size: 12px;
  margin-bottom: 16px;
}

.result-compact .result-actions {
  margin-bottom: 16px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .result-container {
    padding: 32px 16px;
  }

  .result-title {
    font-size: 20px;
  }

  .result-subtitle {
    font-size: 14px;
  }

  .result-description {
    font-size: 12px;
  }

  .result-actions {
    flex-direction: column;
    gap: 12px;
  }

  .result-404-icon,
  .result-403-icon,
  .result-500-icon {
    width: 80px;
    height: 80px;
    font-size: 32px;
  }
}

@media (max-width: 480px) {
  .result-container {
    padding: 24px 12px;
  }

  .result-title {
    font-size: 18px;
  }

  .result-subtitle {
    font-size: 13px;
  }

  .result-404-icon,
  .result-403-icon,
  .result-500-icon {
    width: 60px;
    height: 60px;
    font-size: 24px;
  }
}

/* 暗色主题适配 */
@media (prefers-color-scheme: dark) {
  .result-404-icon,
  .result-403-icon,
  .result-500-icon {
    background: linear-gradient(135deg, #4c63d2 0%, #5a4fcf 100%);
  }
}

/* 动画效果 */
.result-container {
  animation: result-fade-in 0.5s ease-out;
}

@keyframes result-fade-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 图标动画 */
.result-icon {
  animation: result-icon-bounce 0.6s ease-out 0.2s both;
}

@keyframes result-icon-bounce {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* 悬停效果 */
.result-actions .el-button:hover {
  transform: translateY(-2px);
  transition: transform 0.2s ease;
}

/* 无障碍支持 */
.result-container[aria-hidden="true"] {
  pointer-events: none;
}

/* 打印样式 */
@media print {
  .result-container {
    break-inside: avoid;
    background: none;
  }

  .result-actions {
    display: none;
  }
}

/* 高对比度模式 */
@media (prefers-contrast: high) {
  .result-title {
    font-weight: 700;
  }

  .result-404-icon,
  .result-403-icon,
  .result-500-icon {
    border: 2px solid currentColor;
  }
}

/* 减少动画模式 */
@media (prefers-reduced-motion: reduce) {
  .result-container,
  .result-icon {
    animation: none;
  }

  .result-actions .el-button:hover {
    transform: none;
    transition: none;
  }
}
</style>
